import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, StatCard, fmtDate } from "@/components/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Printer, Download, GraduationCap, Clock, CalendarCheck, Award, Plus, Trash2, BookOpenCheck } from "lucide-react";
import { toast } from "sonner";

const PROGRAM_LABEL = { Core: "Core", "Texas TEKS": "Texas TEKS", AP: "AP prep", "SAT & ACT": "SAT/ACT prep", GED: "GED prep", Professional: "Professional", Transfer: "Transfer" };

function TranscriptTab({ sid }) {
  const [t, setT] = useState(null);
  useEffect(() => {
    api.get(`/records/${sid}/transcript`).then(({ data }) => setT(data)).catch((e) => toast.error(errMsg(e)));
  }, [sid]);
  if (!t) return <Loading />;
  const s = t.summary;
  return (
    <div className="space-y-6" data-testid="transcript-tab">
      <div className="flex flex-wrap gap-2 print:hidden">
        <Button onClick={() => window.print()} data-testid="transcript-print-button"><Printer className="h-4 w-4 mr-2" />Print / save as PDF</Button>
      </div>
      <div className="bg-white border border-border rounded-[18px] p-6 sm:p-8 print:border-0 print:p-0" data-testid="transcript-document">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 border-b border-border pb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-display">{t.school.name} · {t.school.type}</div>
            <h2 className="mt-1 text-2xl font-display font-semibold">Academic Transcript</h2>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
              <div><span className="text-muted-foreground">Student:</span> <span className="font-medium" data-testid="transcript-student-name">{t.student.name}</span></div>
              <div><span className="text-muted-foreground">Student ID:</span> <span className="font-mono">{t.student.user_id}</span></div>
              <div><span className="text-muted-foreground">Grade level:</span> {t.student.grade_band || "—"}</div>
              <div><span className="text-muted-foreground">Enrolled:</span> {t.student.enrolled_at ? fmtDate(t.student.enrolled_at) : "—"}</div>
            </div>
          </div>
          <div className="text-sm sm:text-right">
            <div className="text-muted-foreground">Issued</div>
            <div className="font-medium">{fmtDate(t.issued_at)}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
          <div className="rounded-xl bg-[#FBFAF4] border border-border p-3"><div className="text-xs text-muted-foreground">High school GPA</div><div className="text-xl font-display font-semibold" data-testid="transcript-hs-gpa">{s.hs_gpa ?? "—"}</div><div className="text-xs text-muted-foreground">{s.hs_credits_earned} credits earned</div></div>
          <div className="rounded-xl bg-[#FBFAF4] border border-border p-3"><div className="text-xs text-muted-foreground">College GPA</div><div className="text-xl font-display font-semibold">{s.college_gpa ?? "—"}</div><div className="text-xs text-muted-foreground">{s.college_hours_earned} semester hours</div></div>
          <div className="rounded-xl bg-[#FBFAF4] border border-border p-3"><div className="text-xs text-muted-foreground">Courses completed</div><div className="text-xl font-display font-semibold" data-testid="transcript-completed">{s.courses_completed}</div><div className="text-xs text-muted-foreground">{s.courses_in_progress} in progress · avg {s.average_percent ?? "—"}%</div></div>
          <div className="rounded-xl bg-[#FBFAF4] border border-border p-3"><div className="text-xs text-muted-foreground">Attendance {s.school_year}</div><div className="text-xl font-display font-semibold">{s.year_days_present} days</div><div className="text-xs text-muted-foreground">{s.year_hours} instructional hours</div></div>
        </div>

        <div className="mt-6 overflow-x-auto">
          {t.courses.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center" data-testid="transcript-empty">No courses yet. Courses appear here once the student starts them.</div>
          ) : (
            <Table data-testid="transcript-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Year</TableHead><TableHead>Course</TableHead><TableHead>Program</TableHead><TableHead>Level</TableHead>
                  <TableHead>Status</TableHead><TableHead className="text-right">Grade</TableHead><TableHead className="text-right">Credit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {t.courses.map((c, i) => (
                  <TableRow key={c.course_id || c.outside_id || i} data-testid="transcript-row">
                    <TableCell className="whitespace-nowrap">{c.school_year || "—"}</TableCell>
                    <TableCell className="font-medium min-w-[180px]">{c.title}<div className="text-xs text-muted-foreground font-normal">{c.subject}{c.source && c.source !== "Scholar Campus" ? ` · ${c.source}` : ""}</div></TableCell>
                    <TableCell className="text-xs">{PROGRAM_LABEL[c.collection] || c.collection}</TableCell>
                    <TableCell className="text-xs">{c.grade_band || "—"}</TableCell>
                    <TableCell className="text-xs">{c.status === "In progress" ? `In progress (${c.lessons_completed}/${c.lessons_total})` : c.status}</TableCell>
                    <TableCell className="text-right font-semibold">{c.letter || "—"}{c.percent != null && c.letter ? <span className="text-xs text-muted-foreground font-normal"> ({c.percent}%)</span> : null}</TableCell>
                    <TableCell className="text-right text-xs whitespace-nowrap">{c.credit_unit === "non-credit" ? "Non-credit" : `${c.credits_earned} ${c.credit_unit === "semester hours" ? "sem. hrs" : "cr"}`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4 text-xs text-muted-foreground">
          <div>
            <div className="font-medium text-[#123645] mb-1">Grading scale</div>
            {t.grading_scale.map((g) => <span key={g.letter} className="mr-3">{g.letter} {g.min}+ ({g.points.toFixed(1)})</span>)}
            <div className="mt-1">P = completed without a scored quiz. GPA is unweighted and credit-weighted.</div>
          </div>
          <div>
            <div className="font-medium text-[#123645] mb-1">Notes</div>
            Exam-prep courses (AP, SAT/ACT, GED, STAAR, professional) are non-credit and are not College Board-authorized AP courses. {t.school.accreditation}
          </div>
        </div>
      </div>
    </div>
  );
}

function AttendanceTab({ sid }) {
  const [days, setDays] = useState("30");
  const [a, setA] = useState(null);
  useEffect(() => {
    setA(null);
    api.get(`/records/${sid}/attendance`, { params: { days } }).then(({ data }) => setA(data)).catch((e) => toast.error(errMsg(e)));
  }, [sid, days]);

  const exportCsv = () => {
    const lines = ["date,minutes,lesson_minutes,quiz_minutes,tutor_minutes,present"];
    a.days.forEach((d) => lines.push([d.date, d.minutes, d.by_kind.lesson || 0, d.by_kind.quiz || 0, d.by_kind.tutor || 0, d.present ? "yes" : "no"].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url;
    el.download = `attendance-${a.student.name.replace(/\s+/g, "-").toLowerCase()}-${days}d.csv`;
    el.click();
    URL.revokeObjectURL(url);
  };

  if (!a) return <Loading />;
  return (
    <div className="space-y-6" data-testid="attendance-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={CalendarCheck} label={`Days present (last ${days})`} value={a.window_days_present} hint={`Present = ${a.present_threshold_minutes}+ active minutes`} testid="attendance-days-present" />
        <StatCard icon={Clock} label={`Hours (last ${days} days)`} value={(a.window_minutes / 60).toFixed(1)} tone="sky" testid="attendance-hours" />
        <StatCard icon={BookOpenCheck} label={`School year ${a.school_year}`} value={`${a.year_hours} h`} hint={`${a.year_days_present} days present since ${fmtDate(a.school_year_start)}`} tone="green" testid="attendance-year" />
      </div>
      <div className="bg-white border border-border rounded-[14px] p-4">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="font-display font-semibold">Daily log</div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-36 h-9 ml-auto" data-testid="attendance-range-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="30">Last 30 days</SelectItem><SelectItem value="60">Last 60 days</SelectItem><SelectItem value="90">Last 90 days</SelectItem></SelectContent>
          </Select>
          <Button variant="outline" className="h-9" onClick={exportCsv} data-testid="attendance-export-button"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="attendance-table">
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead className="text-right">Active minutes</TableHead><TableHead className="text-right">Lessons</TableHead><TableHead className="text-right">Quizzes</TableHead><TableHead className="text-right">AI tutor</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {a.days.map((d) => (
                <TableRow key={d.date}>
                  <TableCell className="whitespace-nowrap">{new Date(d.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</TableCell>
                  <TableCell className="text-right font-medium">{d.minutes}</TableCell>
                  <TableCell className="text-right">{d.by_kind.lesson || 0}</TableCell>
                  <TableCell className="text-right">{d.by_kind.quiz || 0}</TableCell>
                  <TableCell className="text-right">{d.by_kind.tutor || 0}</TableCell>
                  <TableCell>{d.present ? <span className="text-xs rounded-full px-2 py-0.5 bg-[#DDF7E7] text-[#14663A]">Present</span> : <span className="text-xs text-muted-foreground">{d.minutes ? "Partial" : "No activity"}</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

const EMPTY_CREDIT = { course_name: "", category: "English", credits: "1", grade: "", school_year: "", source: "" };

function GradPlanTab({ sid }) {
  const [g, setG] = useState(null);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CREDIT);
  const [eoc, setEoc] = useState({});
  const load = useCallback(() => api.get(`/records/${sid}/grad-plan`).then(({ data }) => {
    setG(data);
    setEoc(Object.fromEntries(data.eoc.map((e) => [e.key, { status: e.status, score: e.score, date: e.date }])));
  }).catch((e) => toast.error(errMsg(e))), [sid]);
  useEffect(() => { load(); api.get("/records/meta").then(({ data }) => setCats(data.tx_categories)); }, [load]);

  const save = async (body, msg = "Graduation plan saved") => {
    try { await api.put(`/records/${sid}/grad-plan`, body); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const addCredit = async () => {
    if (!form.course_name.trim()) return toast.error("Enter a course name");
    try {
      await api.post(`/records/${sid}/outside-credits`, { ...form, credits: parseFloat(form.credits) });
      toast.success("Credit added");
      setOpen(false);
      setForm(EMPTY_CREDIT);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delCredit = async (id) => {
    try { await api.delete(`/records/${sid}/outside-credits/${id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!g) return <Loading />;
  const pct = Math.min(100, Math.round((g.total_earned * 100) / g.total_required));
  return (
    <div className="space-y-6" data-testid="gradplan-tab">
      <div className="bg-white border border-border rounded-[14px] p-5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-display">{g.program}</div>
            <div className="mt-1 text-2xl font-display font-semibold" data-testid="gradplan-total">{g.total_earned} / {g.total_required} credits</div>
            <Progress value={pct} className="h-2 mt-2 max-w-md" />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <Label className="text-xs">Endorsement</Label>
              <Select value={g.endorsement} onValueChange={(v) => save({ endorsement: v })} disabled={!g.can_edit}>
                <SelectTrigger className="w-56 h-10 mt-1" data-testid="gradplan-endorsement-select"><SelectValue /></SelectTrigger>
                <SelectContent>{g.endorsements.map((e) => <SelectItem key={e} value={e}>{e === "None" ? "Foundation only (no endorsement)" : e}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Switch checked={g.distinguished} onCheckedChange={(v) => save({ distinguished: v })} disabled={!g.can_edit} data-testid="gradplan-distinguished-switch" />
              <span className="text-sm">Distinguished Level of Achievement</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="gradplan-categories">
        {g.categories.map((c) => {
          const p = Math.min(100, Math.round((c.earned * 100) / c.required));
          return (
            <div key={c.category} className="bg-white border border-border rounded-[14px] p-4" data-testid="gradplan-category">
              <div className="flex justify-between text-sm"><span className="font-medium">{c.category}</span><span className={c.earned >= c.required ? "text-[#14663A] font-semibold" : "text-muted-foreground"}>{c.earned} / {c.required}</span></div>
              <Progress value={p} className="h-1.5 mt-2" />
              {c.note && <div className="text-xs text-muted-foreground mt-2">{c.note}</div>}
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-border rounded-[14px] p-5">
        <div className="flex items-center gap-2 mb-3"><Award className="h-4 w-4 text-[#0B6B6B]" /><div className="font-display font-semibold">STAAR end-of-course exams</div></div>
        <div className="overflow-x-auto">
          <Table data-testid="gradplan-eoc-table">
            <TableHeader><TableRow><TableHead>Exam</TableHead><TableHead>Status</TableHead><TableHead>Score</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
            <TableBody>
              {g.eoc.map((e) => (
                <TableRow key={e.key}>
                  <TableCell className="font-medium">{e.exam}</TableCell>
                  <TableCell>
                    <Select value={eoc[e.key]?.status || "not_taken"} onValueChange={(v) => setEoc({ ...eoc, [e.key]: { ...eoc[e.key], status: v } })} disabled={!g.can_edit}>
                      <SelectTrigger className="w-36 h-9" data-testid={`gradplan-eoc-${e.key}-status`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="not_taken">Not taken</SelectItem><SelectItem value="passed">Passed</SelectItem><SelectItem value="not_passed">Not passed</SelectItem></SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell><Input className="h-9 w-24" value={eoc[e.key]?.score || ""} onChange={(ev) => setEoc({ ...eoc, [e.key]: { ...eoc[e.key], score: ev.target.value } })} disabled={!g.can_edit} data-testid={`gradplan-eoc-${e.key}-score`} /></TableCell>
                  <TableCell><Input type="date" className="h-9 w-40" value={eoc[e.key]?.date || ""} onChange={(ev) => setEoc({ ...eoc, [e.key]: { ...eoc[e.key], date: ev.target.value } })} disabled={!g.can_edit} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {g.can_edit && <Button className="mt-3" onClick={() => save({ eoc }, "Exam results saved")} data-testid="gradplan-eoc-save">Save exam results</Button>}
      </div>

      <div className="bg-white border border-border rounded-[14px] p-5">
        <div className="flex items-center gap-2 mb-3">
          <GraduationCap className="h-4 w-4 text-[#0B6B6B]" /><div className="font-display font-semibold">Outside & transfer credits</div>
          {g.can_edit && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button variant="outline" className="ml-auto h-9" data-testid="gradplan-add-credit-button"><Plus className="h-4 w-4 mr-1" />Add credit</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add an outside credit</DialogTitle></DialogHeader>
                <div className="grid gap-3">
                  <div><Label>Course name</Label><Input value={form.course_name} onChange={(e) => setForm({ ...form, course_name: e.target.value })} placeholder="e.g. Physical Education I" data-testid="credit-course-input" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Category</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger data-testid="credit-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Credits</Label><Input type="number" step="0.5" min="0.5" max="4" value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} data-testid="credit-credits-input" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Grade (letter or %)</Label><Input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="A or 93" data-testid="credit-grade-input" /></div>
                    <div><Label>School year</Label><Input value={form.school_year} onChange={(e) => setForm({ ...form, school_year: e.target.value })} placeholder="2025-26" /></div>
                  </div>
                  <div><Label>Where it was earned</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="e.g. Austin Community College dual credit" /></div>
                </div>
                <DialogFooter><Button onClick={addCredit} data-testid="credit-save-button">Add credit</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        {g.outside_credits.length === 0 ? (
          <div className="text-sm text-muted-foreground">No outside credits recorded. Add PE, languages, dual-credit or transfer courses here.</div>
        ) : (
          <ul className="divide-y divide-border" data-testid="gradplan-outside-list">
            {g.outside_credits.map((o) => (
              <li key={o.credit_id} className="py-2 flex items-center gap-3 text-sm">
                <span className="flex-1"><span className="font-medium">{o.course_name}</span> <span className="text-muted-foreground">· {o.category} · {o.credits} cr{o.grade ? ` · ${o.grade}` : ""}{o.school_year ? ` · ${o.school_year}` : ""}</span></span>
                {g.can_edit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => delCredit(o.credit_id)} aria-label="Remove credit" data-testid="gradplan-remove-credit"><Trash2 className="h-4 w-4" /></Button>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-5">{g.notes.map((n) => <li key={n}>{n}</li>)}</ul>
    </div>
  );
}

export default function Records() {
  const { studentId } = useParams();
  const { user } = useAuth();
  const sid = studentId || "me";
  const [name, setName] = useState("");
  useEffect(() => {
    api.get(`/records/${sid}/attendance`, { params: { days: 7 } }).then(({ data }) => setName(data.student.name)).catch(() => {});
  }, [sid]);
  const back = user.role === "parent" && sid !== "me" ? `/children/${sid}` : "/dashboard";
  return (
    <div className="max-w-[1100px]">
      <Button variant="ghost" asChild className="mb-4 -ml-3 print:hidden"><Link to={back}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Link></Button>
      <div className="mb-6 print:hidden">
        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-display">Academic records</div>
        <h1 className="text-3xl font-display font-semibold tracking-tight" data-testid="records-title">{name ? `${name}'s records` : "Records"}</h1>
      </div>
      <Tabs defaultValue="transcript">
        <TabsList className="print:hidden" data-testid="records-tabs">
          <TabsTrigger value="transcript" data-testid="records-tab-transcript">Transcript</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="records-tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="grad" data-testid="records-tab-grad">Texas graduation plan</TabsTrigger>
        </TabsList>
        <TabsContent value="transcript" className="mt-6"><TranscriptTab sid={sid} /></TabsContent>
        <TabsContent value="attendance" className="mt-6"><AttendanceTab sid={sid} /></TabsContent>
        <TabsContent value="grad" className="mt-6"><GradPlanTab sid={sid} /></TabsContent>
      </Tabs>
    </div>
  );
}
